import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useConvex, useMutation, useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import argusLogoText from "../assets/argus-logo-text-2.png";
import { generateTourReportPdfBlob } from "./tourReportPdf";

type TrackTikScan = {
  scanTrackTikId?: number | null;
  checkpointId?: number | null;
  checkpointName?: string;
  checkpointRuleLabel?: string;
  scanTime?: number | string | null;
  scanType?: string | null;
  status?: "scanned" | "missed"; // New: indicates if checkpoint was scanned or missed
};

type TrackTikSession = {
  tourSessionId: number;
  tourName?: string;
  employeeName?: string;
  startTime?: number | string;
  endTime?: number | string | null;
  duration?: number;
  status?: string;
  totalScans?: number;
  totalCheckpointsInTour?: number;
  completionPercentage?: number;
  checkpointNames?: string[];
  scans?: TrackTikScan[];
};

type TourSessionReportData = {
  tourSessionId: number;
  tourName: string;
  employeeName: string;
  startTime: number | null;
  endTime: number | null;
  firstCheckpointScanTime: number | null;
  lastCheckpointScanTime: number | null;
  realTourDurationMinutes: number | null;
  averageSitePatrolMinutes: number | null;
  averageTravelBetweenSitesMinutes: number | null;
  siteDoubleHitCount: number;
  uniqueDoubleHitSiteCount: number;
  siteQuickPatrolCount: number;
  uniqueQuickPatrolSiteCount: number;
  totalCheckpointsInTour: number;
  scannedCheckpointCount: number;
  checkpointCompletionPercentage: number;
  doubleHitScanTrackTikIds?: number[];
  quickPatrolScanTrackTikIds?: number[];
};

type ToursPanelProps = {
  onCheckpointReplay?: (visit: any) => void;
};

export interface ToursPanelHandle {
  /** Step back one level: rule editor → detail → list. Returns true if it stepped back. */
  stepBack(): boolean;
}

type CheckpointRuleType = "before" | "after" | "between";
const PAST_TOURS_PAGE_SIZE = 25;
const MAX_TOUR_LIST_LIMIT = 200;

function safeDate(value: unknown): Date | null {
  if (value === null || value === undefined) return null;
  const d = new Date(value as any);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatTime(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(d: Date | null): string {
  if (!d) return "—";
  return d.toLocaleDateString();
}

function clampPct(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, Math.round(n)));
}

function formatDateTime(d: Date | null): string {
  if (!d) return "—";
  const date = d.toLocaleDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
}

function formatMinutesLabel(value: unknown): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n < 0) return "—";
  const rounded = Math.round(n * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded} min` : `${rounded.toFixed(1)} min`;
}

function sanitizeFileSegment(value: string): string {
  const out = (value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return out || "tour";
}

function openTourReportViewer(pdfBlob: Blob, fileName: string) {
  if (typeof window === "undefined") return;
  const objectUrl = URL.createObjectURL(pdfBlob);
  const safeFileName = fileName.replace(/[^a-zA-Z0-9._-]/g, "_");
  const viewer = window.open("", "_blank");

  if (!viewer) {
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = safeFileName;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2 * 60 * 1000);
    return;
  }

  viewer.document.open();
  viewer.document.write(`<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>${safeFileName}</title>
  <style>
    html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #0f0f0f; color: #f0f0f0; font-family: 'Fira Code', monospace; }
    .toolbar { height: 52px; display: flex; align-items: center; justify-content: space-between; padding: 0 14px; border-bottom: 1px solid rgba(255,255,255,0.12); background: #161616; }
    .title { font-size: 11px; letter-spacing: 0.05em; color: rgba(255,255,255,0.78); text-transform: uppercase; }
    .download { display: inline-flex; align-items: center; gap: 6px; border: 1px solid rgba(255,255,255,0.18); color: #f4f4f4; text-decoration: none; border-radius: 8px; padding: 8px 12px; font-size: 11px; letter-spacing: 0.05em; text-transform: uppercase; }
    .download:hover { border-color: rgba(139,0,0,0.7); background: rgba(139,0,0,0.24); }
    iframe { width: 100%; height: calc(100% - 53px); border: 0; background: #0b0b0b; }
  </style>
</head>
<body>
  <div class="toolbar">
    <div class="title">Tour Report Preview</div>
    <a class="download" href="${objectUrl}" download="${safeFileName}">Download PDF</a>
  </div>
  <iframe src="${objectUrl}#toolbar=1&navpanes=0"></iframe>
</body>
</html>`);
  viewer.document.close();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 15 * 60 * 1000);
}

function minutesToInputValue(minutes: number): string {
  const clamped = Math.max(0, Math.min(1439, Math.floor(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function inputValueToMinutes(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  let hours: number | null = null;
  let minutes: number | null = null;
  const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})$/);
  if (colonMatch) {
    hours = Number(colonMatch[1]);
    minutes = Number(colonMatch[2]);
  } else {
    const digits = trimmed.replace(/\D/g, "");
    if (digits.length === 3 || digits.length === 4) {
      hours = Number(digits.slice(0, digits.length - 2));
      minutes = Number(digits.slice(-2));
    }
  }

  if (hours === null || minutes === null) return null;
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
  return hours * 60 + minutes;
}

function normalizeTimeInputDraft(value: string): string {
  const cleaned = value.replace(/[^\d:]/g, "");
  if (cleaned.includes(":")) {
    const [hoursPart = "", minutesPart = ""] = cleaned.split(":", 2);
    return `${hoursPart.slice(0, 2)}:${minutesPart.slice(0, 2)}`.replace(/:$/, (hoursPart.length >= 2 ? ":" : ""));
  }
  const digits = cleaned.slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function normalizeTimeInputFinal(value: string): string {
  const minutes = inputValueToMinutes(value);
  if (minutes === null) return value.trim();
  return minutesToInputValue(minutes);
}

const ToursPanel = forwardRef<ToursPanelHandle, ToursPanelProps>(function ToursPanel(
  { onCheckpointReplay },
  ref
) {
  const convex = useConvex();
  const [activeTab, setActiveTab] = useState<"active" | "past">("active");
  const [pastLimit, setPastLimit] = useState(PAST_TOURS_PAGE_SIZE);
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [checkpointContextMenu, setCheckpointContextMenu] = useState<{
    x: number;
    y: number;
    checkpointId: number;
    checkpointName: string;
  } | null>(null);
  const [ruleEditorCheckpoint, setRuleEditorCheckpoint] = useState<{
    checkpointId: number;
    fallbackName: string;
  } | null>(null);
  const [ruleType, setRuleType] = useState<CheckpointRuleType>("before");
  const [startTimeValue, setStartTimeValue] = useState("18:00");
  const [endTimeValue, setEndTimeValue] = useState("09:00");
  const [displayNameValue, setDisplayNameValue] = useState("");
  const [isSavingRule, setIsSavingRule] = useState(false);
  const [isDeletingRule, setIsDeletingRule] = useState(false);
  const [ruleSaveError, setRuleSaveError] = useState<string | null>(null);
  const [isGeneratingReportPdf, setIsGeneratingReportPdf] = useState(false);
  const [reportPdfError, setReportPdfError] = useState<string | null>(null);

  useImperativeHandle(ref, () => ({
    stepBack() {
      if (ruleEditorCheckpoint) {
        setRuleEditorCheckpoint(null);
        setRuleSaveError(null);
        setIsSavingRule(false);
        return true;
      }
      if (selectedSessionId !== null) {
        setSelectedSessionId(null);
        setReportPdfError(null);
        setIsGeneratingReportPdf(false);
        return true;
      }
      return false;
    },
  }), [ruleEditorCheckpoint, selectedSessionId]);

  const findReplayVisitForTourCheckpoint = useMutation(
    (api as any).patrolVisits.findReplayVisitForTourCheckpoint
  );
  const upsertCheckpointRule = useMutation((api as any).checkpointRules.upsertCheckpointRule);
  const deleteCheckpointRule = useMutation((api as any).checkpointRules.deleteCheckpointRule);
  const checkpointDisplayNamesRaw = useQuery(
    (api as any).checkpointRules.getCheckpointDisplayNames
  ) as { checkpointId: number; displayName: string }[] | undefined;
  const displayNameMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const entry of checkpointDisplayNamesRaw || []) {
      map.set(entry.checkpointId, entry.displayName);
    }
    return map;
  }, [checkpointDisplayNamesRaw]);
  const checkpointRuleEditor = useQuery(
    (api as any).checkpointRules.getCheckpointRuleEditor,
    ruleEditorCheckpoint ? { checkpointId: ruleEditorCheckpoint.checkpointId } : ("skip" as any)
  ) as
    | {
        checkpointId: number;
        checkpointName: string;
        displayName: string;
        rule?: {
          ruleType: CheckpointRuleType;
          startMinutes: number;
          endMinutes: number | null;
          label: string;
        } | null;
      }
    | undefined;

  // TrackTik tour sessions list (lightweight): subscribe only to the visible tab.
  const activeSessions = useQuery(
    (api as any).trackTikAnalytics?.getTourSessionsList,
    selectedSessionId === null && activeTab === "active"
      ? {
          days: 1,
          limit: 25,
          status: "in_progress",
        }
      : ("skip" as any)
  ) as TrackTikSession[] | undefined;

  const pastSessions = useQuery(
    (api as any).trackTikAnalytics?.getTourSessionsList,
    selectedSessionId === null && activeTab === "past"
      ? {
          days: 7,
          limit: pastLimit,
          status: "completed",
        }
      : ("skip" as any)
  ) as TrackTikSession[] | undefined;

  // Selected tour detail (full checkpoint rows) is loaded only when needed.
  const selectedSession = useQuery(
    (api as any).trackTikAnalytics?.getTourSessionDetail,
    selectedSessionId !== null ? { tourSessionId: selectedSessionId } : ("skip" as any)
  ) as TrackTikSession | null | undefined;

  const activeList = activeSessions || [];
  const pastList = pastSessions || [];
  const baseList = activeTab === "active" ? activeList : pastList;
  const isLoading = activeTab === "active" ? activeSessions === undefined : pastSessions === undefined;
  const canLoadMorePastTours =
    activeTab === "past" &&
    !isLoading &&
    pastLimit < MAX_TOUR_LIST_LIMIT &&
    pastList.length >= pastLimit;

  const filteredAndSorted = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? baseList.filter((s) => {
          const name = (s?.tourName || "").toLowerCase();
          const guard = (s?.employeeName || "").toLowerCase();
          
          // Also search checkpoint names included in the lightweight list payload.
          const hasCheckpoint = (s?.checkpointNames || []).some((checkpointName) =>
            (checkpointName || "").toLowerCase().includes(q)
          );
          
          return name.includes(q) || guard.includes(q) || hasCheckpoint;
        })
      : baseList;

    // Default sort: most recent first
    const sorted = [...filtered].sort((a, b) => {
      const at = safeDate(a?.startTime)?.getTime() ?? 0;
      const bt = safeDate(b?.startTime)?.getTime() ?? 0;
      return bt - at;
    });

    return sorted;
  }, [baseList, searchQuery]);

  useEffect(() => {
    if (!checkpointContextMenu) return;
    const onPointer = () => setCheckpointContextMenu(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setCheckpointContextMenu(null);
    };
    window.addEventListener("click", onPointer);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("click", onPointer);
      window.removeEventListener("keydown", onKey);
    };
  }, [checkpointContextMenu]);

  useEffect(() => {
    if (!ruleEditorCheckpoint || !checkpointRuleEditor) return;
    setDisplayNameValue(checkpointRuleEditor.displayName || checkpointRuleEditor.checkpointName || "");
    const existingRule = checkpointRuleEditor.rule;
    if (!existingRule) {
      setRuleType("before");
      setStartTimeValue("18:00");
      setEndTimeValue("09:00");
      return;
    }
    setRuleType(existingRule.ruleType);
    setStartTimeValue(minutesToInputValue(existingRule.startMinutes));
    if (typeof existingRule.endMinutes === "number") {
      setEndTimeValue(minutesToInputValue(existingRule.endMinutes));
    } else {
      setEndTimeValue("09:00");
    }
  }, [ruleEditorCheckpoint, checkpointRuleEditor]);


  const closeRuleEditor = () => {
    setRuleEditorCheckpoint(null);
    setRuleSaveError(null);
    setIsSavingRule(false);
    setDisplayNameValue("");
  };

  const openRuleEditor = (checkpointId: number, checkpointName: string) => {
    setRuleEditorCheckpoint({
      checkpointId,
      fallbackName: checkpointName || `Checkpoint ${checkpointId}`,
    });
    setRuleSaveError(null);
    setRuleType("before");
    setStartTimeValue("18:00");
    setEndTimeValue("09:00");
    setDisplayNameValue(checkpointName || `Checkpoint ${checkpointId}`);
  };

  const removeCheckpointRule = async () => {
    if (!ruleEditorCheckpoint) return;
    setIsDeletingRule(true);
    setRuleSaveError(null);
    try {
      await deleteCheckpointRule({ checkpointId: ruleEditorCheckpoint.checkpointId });
      closeRuleEditor();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setRuleSaveError(message || "CHECKPOINT-RULE-UI-004 Failed to remove rule.");
    } finally {
      setIsDeletingRule(false);
    }
  };

  const handleGenerateTourReportPdf = async (session: TrackTikSession, scans: TrackTikScan[]) => {
    const reportQuery = (api as any).trackTikAnalytics?.getTourSessionReportData;
    if (!reportQuery) {
      setReportPdfError("Tour report endpoint is unavailable.");
      return;
    }

    setIsGeneratingReportPdf(true);
    setReportPdfError(null);

    try {
      const reportData = (await convex.query(reportQuery, {
        tourSessionId: session.tourSessionId,
      })) as TourSessionReportData | null;

      if (!reportData) {
        throw new Error("Tour report data could not be loaded.");
      }

      const dhSet = new Set(reportData.doubleHitScanTrackTikIds ?? []);
      const qpSet = new Set(reportData.quickPatrolScanTrackTikIds ?? []);

      const checkpoints = scans.map((scan, index) => {
        const cpId = typeof scan.checkpointId === "number" && scan.checkpointId > 0 ? scan.checkpointId : 0;
        const rawName = scan.checkpointName || "Checkpoint";
        const checkpointName = (cpId > 0 && displayNameMap.get(cpId)) || rawName;
        const isScanned = scan.status === "scanned";
        const when = isScanned ? safeDate(scan.scanTime) : null;
        const scanId = typeof scan.scanTrackTikId === "number" && scan.scanTrackTikId > 0 ? scan.scanTrackTikId : 0;
        return {
          index: index + 1,
          checkpointName,
          status: (isScanned ? "Scanned" : "Missed") as "Scanned" | "Missed",
          scanTimeLabel: formatDateTime(when),
          isDoubleHit: scanId > 0 && dhSet.has(scanId),
          isQuickPatrol: scanId > 0 && qpSet.has(scanId),
        };
      });

      const scheduledStart = safeDate(session.startTime);
      const firstCheckpoint = safeDate(reportData.firstCheckpointScanTime);
      const actualReportDateTime = firstCheckpoint || scheduledStart || safeDate(reportData.startTime) || new Date();
      const checkpointCompletionLabel = `${reportData.checkpointCompletionPercentage}% (${reportData.scannedCheckpointCount}/${reportData.totalCheckpointsInTour})`;
      const siteDoubleHitsLabel =
        reportData.uniqueDoubleHitSiteCount > 0
          ? `${reportData.siteDoubleHitCount} events across ${reportData.uniqueDoubleHitSiteCount} sites`
          : `${reportData.siteDoubleHitCount} events`;
      const siteQuickPatrolsLabel =
        reportData.uniqueQuickPatrolSiteCount > 0
          ? `${reportData.siteQuickPatrolCount} events across ${reportData.uniqueQuickPatrolSiteCount} sites`
          : `${reportData.siteQuickPatrolCount} events`;

      const pdfBlob = await generateTourReportPdfBlob({
        logoSrc: argusLogoText,
        hqAddressLines: [
          "602 14th Street",
          "Modesto California 95354",
          "United States of America (the)",
        ],
        reportDateLabel: actualReportDateTime.toLocaleDateString(),
        reportTimeLabel: actualReportDateTime.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        tourName: session.tourName || "Tour Session",
        guardName: session.employeeName || "Unknown guard",
        tourSessionId: session.tourSessionId,
        averageSitePatrolTimeLabel: formatMinutesLabel(reportData.averageSitePatrolMinutes),
        averageTravelTimeLabel: formatMinutesLabel(reportData.averageTravelBetweenSitesMinutes),
        realTourDurationLabel: formatMinutesLabel(reportData.realTourDurationMinutes),
        siteDoubleHitsLabel,
        siteQuickPatrolsLabel,
        checkpointCompletionLabel,
        checkpoints,
      });

      const fileName = `tour-report-${sanitizeFileSegment(session.tourName || "tour")}-${session.tourSessionId}.pdf`;
      openTourReportViewer(pdfBlob, fileName);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setReportPdfError(message || "Failed to generate tour report PDF.");
    } finally {
      setIsGeneratingReportPdf(false);
    }
  };

  const saveCheckpointRule = async () => {
    if (!ruleEditorCheckpoint) return;
    const startMinutes = inputValueToMinutes(startTimeValue);
    const endMinutes = inputValueToMinutes(endTimeValue);
    if (startMinutes === null) {
      setRuleSaveError("CHECKPOINT-RULE-UI-001 Start time is invalid.");
      return;
    }
    if (ruleType === "between" && endMinutes === null) {
      setRuleSaveError("CHECKPOINT-RULE-UI-002 End time is required for between rules.");
      return;
    }

    setRuleSaveError(null);
    setIsSavingRule(true);
    try {
      await upsertCheckpointRule({
        checkpointId: ruleEditorCheckpoint.checkpointId,
        ruleType,
        startMinutes,
        endMinutes: ruleType === "between" ? endMinutes ?? undefined : undefined,
        displayName: displayNameValue.trim() || undefined,
      });
      closeRuleEditor();
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setRuleSaveError(message || "CHECKPOINT-RULE-UI-003 Failed to save rule.");
    } finally {
      setIsSavingRule(false);
    }
  };

  const handleCheckpointReplay = async (tourSessionId: number, scan: TrackTikScan) => {
    if (!onCheckpointReplay) return;
    if (scan.status !== "scanned") return;

    const parsedScanTime = safeDate(scan.scanTime)?.getTime();

    try {
      const replayVisit = await findReplayVisitForTourCheckpoint({
        tourSessionId,
        scanTrackTikId:
          typeof scan.scanTrackTikId === "number" && scan.scanTrackTikId > 0
            ? scan.scanTrackTikId
            : undefined,
        checkpointId:
          typeof scan.checkpointId === "number" && scan.checkpointId > 0
            ? scan.checkpointId
            : undefined,
        checkpointName: scan.checkpointName,
        scanTime: typeof parsedScanTime === "number" ? parsedScanTime : undefined,
      });

      if (replayVisit) {
        onCheckpointReplay(replayVisit);
      }
    } catch (error) {
      console.error("Failed to find patrol replay visit for checkpoint:", error);
    }
  };

  // ---------------- Detail view ----------------
  if (selectedSessionId !== null) {
    if (selectedSession === undefined) {
      return (
        <div className="tours-panel tt-tours">
          <div className="loading">
            <div className="spinner"></div>
            Loading tour details...
          </div>
        </div>
      );
    }
    if (!selectedSession) {
      return (
        <div className="tours-panel tt-tours">
          <div className="tt-empty">Tour details could not be found.</div>
        </div>
      );
    }
    const start = safeDate(selectedSession.startTime);
    const end = safeDate(selectedSession.endTime);
    const scans = Array.isArray(selectedSession.scans) ? selectedSession.scans : [];
    const totalScans = selectedSession.totalScans ?? scans.length ?? 0;
    const totalStops = selectedSession.totalCheckpointsInTour || totalScans || 0;
    const completion = clampPct(selectedSession.completionPercentage);
    const isActive = selectedSession.status === "in_progress";
    const scannedCount = scans.filter((s) => s?.status === "scanned").length;
    const checkpointRuleName =
      checkpointRuleEditor?.checkpointName ||
      ruleEditorCheckpoint?.fallbackName ||
      (ruleEditorCheckpoint ? `Checkpoint ${ruleEditorCheckpoint.checkpointId}` : "Checkpoint");
    const existingRuleLabel = checkpointRuleEditor?.rule?.label || "";

    // ---------------- Checkpoint rule editor (inline sidebar page) ----------------
    if (ruleEditorCheckpoint) {
      return (
        <div className="tours-panel tt-tours tt-tours-detail">
          <div className="tt-tour-detail-header">
            <div className="tt-tour-detail-heading">
              <div className="tt-tour-detail-title">Checkpoint Rule</div>
              <div className="tt-tour-detail-sub">
                <span className="tt-sub-item">{checkpointRuleName}</span>
              </div>
            </div>
          </div>

          <div className="tt-tour-detail-body">
            {/* Checkpoint info */}
            <div className="tt-checkpoint-rule-meta">
              <input
                type="text"
                className="tt-checkpoint-rule-meta-name-input"
                value={displayNameValue}
                onChange={(e) => setDisplayNameValue(e.target.value)}
                placeholder="Display name..."
                aria-label="Checkpoint display name"
              />
              <div className="tt-checkpoint-rule-meta-id">ID · {ruleEditorCheckpoint.checkpointId}</div>
              <div className={`tt-checkpoint-rule-meta-status${existingRuleLabel ? " has-rule" : ""}`}>
                {existingRuleLabel ? (
                  <>
                    <span>Active: {existingRuleLabel}</span>
                    <button
                      type="button"
                      className="tt-checkpoint-rule-remove-btn"
                      onClick={() => void removeCheckpointRule()}
                      disabled={isDeletingRule}
                      aria-label="Remove checkpoint rule"
                      title="Remove rule"
                    >
                      {isDeletingRule ? "…" : "×"}
                    </button>
                  </>
                ) : "No rule set"}
              </div>
            </div>

            {checkpointRuleEditor === undefined ? (
              <div className="tt-checkpoint-rule-loading">
                <div className="spinner" style={{ width: 16, height: 16 }} />
                Loading rule...
              </div>
            ) : (
              <>
                {/* Rule type */}
                <div className="tt-tour-overview">
                  <div className="tt-checkpoint-rule-section-label" style={{ marginBottom: 10 }}>Rule Type</div>
                  <div className="tt-checkpoint-rule-options">
                    {(["before", "after", "between"] as CheckpointRuleType[]).map((type) => (
                      <label
                        key={type}
                        className={`tt-checkpoint-rule-option${ruleType === type ? " selected" : ""}`}
                      >
                        <input
                          type="radio"
                          name="checkpoint-rule-type"
                          checked={ruleType === type}
                          onChange={() => setRuleType(type)}
                          style={{ display: "none" }}
                        />
                        <div className="tt-checkpoint-rule-option-dot">
                          <div className="tt-checkpoint-rule-option-dot-inner" />
                        </div>
                        <span>
                          {type === "before" && "Must be scanned before"}
                          {type === "after" && "Must be scanned after"}
                          {type === "between" && "Must be scanned between"}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Time inputs */}
                <div className="tt-tour-overview">
                  <div className="tt-checkpoint-rule-time-section">
                    <div className="tt-checkpoint-rule-section-label" style={{ marginBottom: 8 }}>
                      {ruleType === "between" ? "Time Window" : "Time"}
                    </div>
                    <div className="tt-checkpoint-rule-time-row">
                      <div className="tt-checkpoint-rule-time-wrap">
                        {ruleType === "between" && (
                          <div className="tt-checkpoint-rule-time-wrap-label">Start</div>
                        )}
                        <input
                          type="text"
                          value={startTimeValue}
                          onChange={(event) => setStartTimeValue(normalizeTimeInputDraft(event.target.value))}
                          onBlur={() => setStartTimeValue((prev) => normalizeTimeInputFinal(prev))}
                          className="tt-checkpoint-rule-time"
                          inputMode="numeric"
                          placeholder="HH:MM"
                          maxLength={5}
                          title="Use 24-hour time (HH:MM)"
                          aria-label={ruleType === "between" ? "Start time" : "Time"}
                        />
                      </div>
                      {ruleType === "between" ? (
                        <>
                          <span className="tt-checkpoint-rule-time-sep">–</span>
                          <div className="tt-checkpoint-rule-time-wrap">
                            <div className="tt-checkpoint-rule-time-wrap-label">End</div>
                            <input
                              type="text"
                              value={endTimeValue}
                              onChange={(event) => setEndTimeValue(normalizeTimeInputDraft(event.target.value))}
                              onBlur={() => setEndTimeValue((prev) => normalizeTimeInputFinal(prev))}
                              className="tt-checkpoint-rule-time"
                              inputMode="numeric"
                              placeholder="HH:MM"
                              maxLength={5}
                              title="Use 24-hour time (HH:MM)"
                              aria-label="End time"
                            />
                          </div>
                        </>
                      ) : null}
                    </div>
                    <div className="tt-checkpoint-rule-time-help">Use 24-hour format (HH:MM)</div>
                  </div>
                </div>

                {/* Live preview */}
                <div className="tt-checkpoint-rule-preview">
                  <strong>{displayNameValue || checkpointRuleName}</strong> must be scanned{" "}
                  {ruleType === "before" && <>before <strong>{startTimeValue || "—"}</strong></>}
                  {ruleType === "after" && <>after <strong>{startTimeValue || "—"}</strong></>}
                  {ruleType === "between" && (
                    <>between <strong>{startTimeValue || "—"}</strong> and <strong>{endTimeValue || "—"}</strong></>
                  )}
                </div>

                {ruleSaveError ? <div className="tt-checkpoint-rule-error">{ruleSaveError}</div> : null}

                <button
                  type="button"
                  className="tt-checkpoint-rule-save"
                  onClick={() => void saveCheckpointRule()}
                  disabled={isSavingRule}
                >
                  {isSavingRule ? "Updating..." : "Update Checkpoint"}
                </button>
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="tours-panel tt-tours tt-tours-detail">
        <div className="tt-tour-detail-header">
          <div className="tt-tour-detail-heading">
            <div className="tt-tour-detail-title-row">
              <div className="tt-tour-detail-title">{selectedSession.tourName || "Tour Session"}</div>
              <span className={`tt-status-badge ${isActive ? "active" : "completed"}`}>
                {isActive ? "Active" : "Completed"}
              </span>
            </div>
            <div className="tt-tour-detail-sub">
              <span className="tt-sub-item">{selectedSession.employeeName || "Unknown guard"}</span>
              <span className="tt-sub-dot">•</span>
              <span className="tt-sub-item">
                {formatTime(start)}
                {!end && isActive ? <span className="tt-live-dot"> • ongoing</span> : null}
              </span>
              <span className="tt-sub-dot">•</span>
              <span className="tt-sub-item">{(selectedSession.duration ?? 0) || 0} min</span>
            </div>
            <div className="tt-tour-detail-date">{formatDate(start)}</div>
          </div>
        </div>

        <div className="tt-tour-detail-body">
          <div className="tt-tour-overview">
            <div className="tt-tour-overview-row">
              <div className="tt-kpi">
                <div className="tt-kpi-value">{completion}%</div>
                <div className="tt-kpi-label">Completion</div>
              </div>
              <div className="tt-kpi">
                <div className="tt-kpi-value">
                  {totalScans}/{totalStops}
                </div>
                <div className="tt-kpi-label">Stops</div>
              </div>
              <div className="tt-kpi">
                <div className="tt-kpi-value">{scans.length}</div>
                <div className="tt-kpi-label">Scans</div>
              </div>
            </div>

            <div className="tt-progress">
              <div className="tt-progress-bar">
                <div
                  className={`tt-progress-fill ${isActive ? "active" : "completed"}`}
                  style={{ width: `${completion}%` }}
                />
              </div>
              <div className="tt-progress-caption">
                <span className="tt-progress-caption-left">Progress</span>
                <span className="tt-progress-caption-right">
                  {totalScans} / {totalStops}
                </span>
              </div>
            </div>
          </div>

          <div className="tt-tour-scans">
            <div className="tt-section-title">
              Tour Route <span className="tt-section-count">({scannedCount}/{totalStops} checkpoints)</span>
            </div>

            {scans.length === 0 ? (
              <div className="tt-empty">No checkpoint data available.</div>
            ) : (
              <div className="tt-scan-list">
                {scans.map((scan, index) => {
                  const when = safeDate(scan.scanTime);
                  const type = scan.scanType && scan.scanType !== "UNKNOWN" ? scan.scanType : null;
                  const isScanned = scan.status === "scanned";
                  const isMissed = scan.status === "missed";
                  const isReplayClickable = isScanned && !!onCheckpointReplay;
                  const statusLabel = isScanned ? "Scanned" : isMissed ? "Missed" : "Unknown";
                  const statusClass = isScanned
                    ? "tt-scan-scanned"
                    : isMissed
                      ? "tt-scan-missed"
                      : "tt-scan-unknown";
                  const rawCheckpointName = scan.checkpointName || "Checkpoint";
                  const cpId = typeof scan.checkpointId === "number" && scan.checkpointId > 0 ? scan.checkpointId : 0;
                  const checkpointName = (cpId > 0 && displayNameMap.get(cpId)) || rawCheckpointName;
                  const checkpointRuleLabel = String(scan.checkpointRuleLabel || "").trim();
                  const checkpointHoverText = checkpointRuleLabel
                    ? `${checkpointName} • Rule: ${checkpointRuleLabel}`
                    : checkpointName;
                  
                  return (
                    <div 
                      key={`${index}-${scan.checkpointName || "scan"}`} 
                      className={`tt-scan-row ${statusClass}`}
                      title={checkpointHoverText}
                      onContextMenu={(event) => {
                        const checkpointId =
                          typeof scan.checkpointId === "number" && scan.checkpointId > 0
                            ? scan.checkpointId
                            : 0;
                        if (checkpointId <= 0) return;
                        event.preventDefault();
                        setCheckpointContextMenu({
                          x: event.clientX,
                          y: event.clientY,
                          checkpointId,
                          checkpointName: (displayNameMap.get(checkpointId)) || scan.checkpointName || `Checkpoint ${checkpointId}`,
                        });
                      }}
                      onClick={
                        isReplayClickable
                          ? () => {
                              void handleCheckpointReplay(selectedSession.tourSessionId, scan);
                            }
                          : undefined
                      }
                      onKeyDown={
                        isReplayClickable
                          ? (event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                void handleCheckpointReplay(selectedSession.tourSessionId, scan);
                              }
                            }
                          : undefined
                      }
                      role={isReplayClickable ? "button" : undefined}
                      tabIndex={isReplayClickable ? 0 : undefined}
                    >
                      <div className="tt-scan-main">
                        <div className="tt-scan-title">
                          <span className="tt-scan-index">{index + 1}</span>
                          <span className="tt-scan-name">{checkpointName}</span>
                          <span className="tt-scan-flex-spacer" />
                          <span
                            className={`tt-scan-status ${
                              isScanned ? "tt-scan-status-scanned" : isMissed ? "tt-scan-status-missed" : "tt-scan-status-unknown"
                            }`}
                          >
                            {isScanned ? "✓" : isMissed ? "✕" : "•"} {statusLabel}
                          </span>
                        </div>
                        <div className="tt-scan-sub">
                          {isScanned ? (
                            <>
                              <span>{formatTime(when)}</span>
                              {type ? <span className="tt-scan-type">• {type}</span> : null}
                            </>
                          ) : (
                            <span className="tt-scan-not-scanned">Not scanned</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!isActive ? (
            <div className="tt-tour-report-actions">
              {reportPdfError ? <div className="tt-tour-report-error">{reportPdfError}</div> : null}
              <button
                type="button"
                className="load-more-btn tt-tour-report-btn"
                onClick={() => void handleGenerateTourReportPdf(selectedSession, scans)}
                disabled={isGeneratingReportPdf}
              >
                {isGeneratingReportPdf ? "Generating Tour Report PDF..." : "Generate Tour Report PDF"}
              </button>
            </div>
          ) : null}
        </div>
        {checkpointContextMenu && typeof document !== "undefined"
          ? createPortal(
              <div
                className="tt-checkpoint-context-menu"
                style={{ top: checkpointContextMenu.y, left: checkpointContextMenu.x }}
                role="menu"
                aria-label="Checkpoint actions"
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="tt-checkpoint-context-item"
                  onClick={() => {
                    openRuleEditor(
                      checkpointContextMenu.checkpointId,
                      checkpointContextMenu.checkpointName
                    );
                    setCheckpointContextMenu(null);
                  }}
                >
                  Edit Checkpoint
                </button>
              </div>,
              document.body
            )
          : null}
      </div>
    );
  }

  // ---------------- List view ----------------
  return (
    <div className="tours-panel tt-tours">
      <div className="tt-tours-toolbar">
        <div className="tt-tours-toolbar-row">
          <input
            className="tt-search"
            type="text"
            placeholder="Search for a tour..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <div className="tt-segmented" role="tablist" aria-label="Tours tabs">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "active"}
              className={`tt-segment ${activeTab === "active" ? "active" : ""}`}
              onClick={() => setActiveTab("active")}
            >
              Active
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "past"}
              className={`tt-segment ${activeTab === "past" ? "active" : ""}`}
              onClick={() => setActiveTab("past")}
            >
              Past
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner"></div>
          Loading tours...
        </div>
      ) : filteredAndSorted.length === 0 ? (
        <div className="tt-empty">
          {activeTab === "active" ? "No active tours right now." : "No past tours in the last 7 days."}
        </div>
      ) : (
        <div className="tt-tour-list">
          {filteredAndSorted.map((session) => {
            const start = safeDate(session.startTime);
            const end = safeDate(session.endTime);
            const completion = clampPct(session.completionPercentage);
            const isActive = session.status === "in_progress";
            const totalScans = session.totalScans ?? session.scans?.length ?? 0;
            const totalStops = session.totalCheckpointsInTour || totalScans || 0;

            return (
              <button
                key={session.tourSessionId}
                type="button"
                className="tt-tour-card"
                onClick={() => setSelectedSessionId(session.tourSessionId)}
                title={session.tourName || "Tour"}
              >
                <div className="tt-card-top">
                  <div className="tt-card-title-wrap">
                    <div className="tt-card-title">{session.tourName || "Tour"}</div>
                    <div className="tt-card-sub">{session.employeeName || "Unknown guard"}</div>
                  </div>
                  <span className={`tt-status-pill ${isActive ? "active" : "completed"}`}>
                    {isActive ? "Active" : "Completed"}
                  </span>
                </div>

                <div className="tt-card-meta">
                  <span className="tt-meta-item">{formatDate(start)}</span>
                  <span className="tt-meta-dot">•</span>
                  <span className="tt-meta-item">
                    {formatTime(start)}
                    {!end && isActive ? <span className="tt-live-dot"> • ongoing</span> : null}
                  </span>
                  <span className="tt-meta-dot">•</span>
                  <span className="tt-meta-item">{(session.duration ?? 0) || 0} min</span>
                </div>

                <div className="tt-card-progress">
                  <div className="tt-progress-bar">
                    <div
                      className={`tt-progress-fill ${isActive ? "active" : "completed"}`}
                      style={{ width: `${completion}%` }}
                    />
                  </div>
                  <div className="tt-card-bottom">
                    <div className="tt-card-bottom-left">
                      <span className="tt-card-bottom-primary">{completion}%</span>
                      <span className="tt-card-bottom-secondary">
                        {totalScans}/{totalStops} stops
                      </span>
                    </div>
                    <span className="tt-card-chevron" aria-hidden="true">
                      →
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      {canLoadMorePastTours ? (
        <button
          type="button"
          className="load-more-btn"
          onClick={() => setPastLimit((prev) => Math.min(MAX_TOUR_LIST_LIMIT, prev + PAST_TOURS_PAGE_SIZE))}
        >
          Load 25 More Past Tours
        </button>
      ) : null}
      {activeTab === "past" && !isLoading ? (
        <div className="patrol-count">{`Loaded ${pastList.length} past tours`}</div>
      ) : null}
    </div>
  );
});

export default ToursPanel;
